import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import {Button, Descriptions, message, Select, Skeleton, Space, Tag, Typography} from "antd";
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import {ROUTES} from "../../components/AppRouter/AppRouter";
import {TRANSACTION_STATUS} from "../../utils/constants";
import {CloseCircleOutlined} from "@ant-design/icons";

const getStatusLabel = (status) => {
  const colorMap = {
    [TRANSACTION_STATUS.APPROVED]: '#52c41a',
    [TRANSACTION_STATUS.DONE]: '#3c8d13',
    [TRANSACTION_STATUS.DECLINED]: '#f5222d',
    [TRANSACTION_STATUS.PENDING]: '#009be2',
    [TRANSACTION_STATUS.NEW]: '#8f8f8f',
  };
  return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
};

const TransactionPreview = () => {
  const [editStatusActive, setEditStatusActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  const {id} = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await API.post(NEW_ENDPOINTS.ADMIN.TRANSACTIONS.INFO, {
          id,
        });
        setData(data[0]);
      } catch (e) {
        //
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id]);

  const handleChangeStatus = async (new_status) => {
    try {
      await API.post(NEW_ENDPOINTS.ADMIN.ORDER.UPDATE, {
        order_id: id,
        status: new_status,
      });
      setData(prev => ({...prev, status: new_status}));
      setEditStatusActive(false);
    } catch (e) {
      message.error(`Failed to update status for id ${id}`);
    }
  };

  const handleOpenDocument = (data) => {
    const byteCharacters = atob(data.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    const blobURL = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobURL;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(blobURL);
  };

  const renderAddressFields = (addressObj) => {
    if (!addressObj) return <div>-/-</div>;

    const fields = [
      { label: 'Country', value: addressObj.country },
      { label: 'Postcode', value: addressObj.postCode },
      { label: 'Town', value: addressObj.town },
      { label: 'Street', value: addressObj.street },
      { label: 'Sub Street', value: addressObj.subStreet },
      { label: 'State', value: addressObj.state },
      { label: 'Building Name', value: addressObj.buildingName },
      { label: 'Flat Number', value: addressObj.flatNumber },
      { label: 'Building Number', value: addressObj.buildingNumber },
      { label: 'Formatted Address', value: addressObj.formattedAddress },
    ];

    return fields.map((field, index) => (
      field.value ? (
        <div key={index}>
          <strong>{field.label}:</strong> {field.value}
        </div>
      ) : null
    ));
  };

  return (
    <Container>
      <Block>
        {loading ? (
          <Skeleton active />
        ) : data && (
          <div>
            <Typography.Title level={2}>Transaction Details</Typography.Title>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="ID">
                <Typography.Text copyable>{data.id}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Creator ID">
                <Typography.Link onClick={() => navigate(`${ROUTES.USER.path}/${data.creator_id}`)} copyable ellipsis>
                  {data.creator_id}
                </Typography.Link>
              </Descriptions.Item>
              <Descriptions.Item label="Type">{data.type}</Descriptions.Item>
              <Descriptions.Item label="Payment System">{data.payment_system}</Descriptions.Item>
              <Descriptions.Item label="Sumsub ID">{data.sumsub_id ?? '-/-'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {editStatusActive ? (
                  <Space>
                    <Select
                      defaultValue={data.status}
                      loading={loading}
                      style={{width: 100}}
                      onChange={(value) => handleChangeStatus(value)}
                      options={Object.values(TRANSACTION_STATUS).map(i => ({title: i, value: i}))}
                    />
                    <CloseCircleOutlined onClick={() => setEditStatusActive(false)} style={{cursor: 'pointer'}}/>
                  </Space>
                ) : (
                  <div onClick={() => setEditStatusActive(true)} style={{cursor: 'pointer'}}>
                    {getStatusLabel(data.status)}
                    <Typography.Text type="secondary">Click to edit</Typography.Text>
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">{data.amount} {data.currency}</Descriptions.Item>
              <Descriptions.Item label="Fee">{data.fee} EUR</Descriptions.Item>
              <Descriptions.Item label="Fee Bank">{data.fee_tesla} EUR</Descriptions.Item>
              <Descriptions.Item label="Sender">
                <div><strong>Name:</strong> {data.from_name ?? '-/-'}</div>
                <div><strong>Account:</strong> {data.from_account ?? '-/-'}</div>
                <div><strong>BIC:</strong> {data.from_bic ?? '-/-'}</div>
                <div><strong>Address:</strong> {[data.from_address_country, data.from_address_city, data.from_address, data.from_address_num, data.from_address_zip].filter(Boolean).join(', ') ?? '-/-'}</div>
                <div><strong>Reference:</strong> {data.from_reference ?? '-/-'}</div>
              </Descriptions.Item>
              <Descriptions.Item label="From Bank Name">
                {data.from_bank_name ?? '-/-'}
              </Descriptions.Item>
              <Descriptions.Item label="From Bank Address">
                {renderAddressFields(data.from_bank_address)}
              </Descriptions.Item>
              <Descriptions.Item label="Beneficiary">
                <div><strong>Name:</strong> {data.to_name ?? '-/-'}</div>
                <div><strong>Account:</strong> {data.to_account ?? '-/-'}</div>
                <div><strong>BIC:</strong> {data.to_bic ?? '-/-'}</div>
                <div><strong>Address:</strong> {[data.to_address_country, data.to_address_city, data.to_address, data.to_address_num, data.to_address_zip].filter(Boolean).join(', ') ?? '-/-'}</div>
                <div><strong>Reference:</strong> {data.to_reference ?? '-/-'}</div>
              </Descriptions.Item>
              <Descriptions.Item label="To Bank Name">
                {data.to_bank_name ?? '-/-'}
              </Descriptions.Item>
              <Descriptions.Item label="To Bank Address">
                {renderAddressFields(data.to_bank_address)}
              </Descriptions.Item>
              <Descriptions.Item label="Payment ID">
                <Typography.Text copyable={data.payment_id}>{data.payment_id ?? '-/-'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Sumsub ID">
                <Typography.Text copyable={data.sumsub_id}>{data.sumsub_id ?? '-/-'}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Done At">{new Date(data.doneAt).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Created At">{new Date(data.createdAt).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Updated At">{new Date(data.updatedAt).toLocaleString()}</Descriptions.Item>
              {data.docs && data.docs.length > 0 && (
                <Descriptions.Item label="Documents">
                  <Space direction="vertical">
                    {data.docs.map(doc => (
                      <Button key={doc.id} onClick={() => handleOpenDocument(doc.data)}>
                        Open {doc.name}
                      </Button>
                    ))}
                  </Space>
                </Descriptions.Item>)}
              <Descriptions.Item label="Payment Description">{data.description}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Block>
    </Container>
  );
};

export default TransactionPreview;
